import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  errorCode,
  specError,
  useApproval,
  useChooseForLearner,
  useLearner,
  useResetChoice,
} from '../../hooks/useSpecialization';
import { INPUT, PRIMARY, SECONDARY, formatDateTime, plural } from './specText';

// keepStarted is undefined for staff who can't read the cohort's settings.
const RESET_TEXT = {
  true: 'Their enrolment in this track’s modules is removed, except modules they’ve started. ',
  false: 'Their enrolment in this track’s modules is removed. ',
  undefined:
    'Their enrolment in this track’s modules is removed (modules they’ve started may stay, depending on the cohort’s settings). ',
};

function Heading({ children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </p>
  );
}

function RuleList({ rules }) {
  if (!rules?.length) return null;
  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {rules.map((rule) => (
        <li key={rule.type} className="flex gap-2">
          {rule.met ? (
            <CheckCircleIcon
              sx={{ fontSize: 18 }}
              className="mt-0.5 text-green-600"
            />
          ) : (
            <RadioButtonUncheckedIcon
              sx={{ fontSize: 18 }}
              className="mt-0.5 text-gray-400"
            />
          )}
          <div>
            <p className="text-sm text-gray-900">{rule.label}</p>
            <p className="text-xs text-gray-500">{rule.message}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ApprovalPanel({ cohortId, studentId, approval }) {
  const mutation = useApproval(cohortId);
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => setConfirmRemove(false), [studentId]);

  const run = (approve) =>
    mutation.mutate(
      { studentId, approve },
      {
        onSuccess: () => {
          setConfirmRemove(false);
          toast.success(approve ? 'Learner approved.' : 'Approval removed.');
        },
        onError: (error) =>
          toast.error(specError(error, "We couldn't change the approval.")),
      }
    );

  return (
    <section className="space-y-2">
      <Heading>Approval</Heading>
      {approval?.approved ? (
        <p className="text-sm text-gray-700">
          Approved
          {approval.approved_by && ` by ${approval.approved_by}`}
          {approval.approved_at &&
            ` on ${formatDateTime(approval.approved_at)}`}
          .
        </p>
      ) : (
        <p className="text-sm text-gray-700">Not approved yet.</p>
      )}
      {!approval?.approved && (
        <button
          type="button"
          onClick={() => run(true)}
          disabled={mutation.isLoading}
          className={PRIMARY}
        >
          Approve
        </button>
      )}
      {approval?.approved && !confirmRemove && (
        <button
          type="button"
          onClick={() => setConfirmRemove(true)}
          className={SECONDARY}
        >
          Remove approval…
        </button>
      )}
      {approval?.approved && confirmRemove && (
        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <p className="text-sm text-gray-700">
            They stop qualifying at once if approval is one of the rules they
            need. Choices already made stay.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => run(false)}
              disabled={mutation.isLoading}
              className={PRIMARY}
            >
              Remove approval
            </button>
            <button
              type="button"
              onClick={() => setConfirmRemove(false)}
              className={SECONDARY}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function SelectionList({ cohortId, studentId, selections, keepStarted }) {
  const reset = useResetChoice(cohortId);
  const [confirmTrack, setConfirmTrack] = useState(null);
  useEffect(() => setConfirmTrack(null), [studentId]);

  const undo = (trackId) =>
    reset.mutate(
      { studentId, trackId },
      {
        onSuccess: (result) => {
          setConfirmTrack(null);
          const kept = result?.kept_module_ids?.length ?? 0;
          toast.success(
            kept
              ? `Their choice is cleared. ${plural(
                  kept,
                  'module'
                )} they've started ${
                  kept === 1 ? 'stays' : 'stay'
                } on their account.`
              : 'Their choice is cleared. They can choose again.'
          );
        },
        onError: (error) => {
          setConfirmTrack(null);
          toast.error(specError(error, "We couldn't undo that choice."));
        },
      }
    );

  if (!selections?.length) {
    return (
      <p className="text-sm text-gray-500">No specialization chosen yet.</p>
    );
  }
  return (
    <ul className="m-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200 p-0">
      {selections.map((selection) => (
        <li key={selection.track_id} className="space-y-2 px-3 py-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm text-gray-900">
                {selection.track_name || selection.track_id}
              </p>
              <p className="text-xs text-gray-500">
                {selection.chosen_by
                  ? `Chosen for them by ${selection.chosen_by}`
                  : 'Chosen by the learner'}
                {selection.selected_at &&
                  `, ${formatDateTime(selection.selected_at)}`}
              </p>
            </div>
            {confirmTrack !== selection.track_id && (
              <button
                type="button"
                onClick={() => setConfirmTrack(selection.track_id)}
                className="text-xs text-claret-500 hover:underline"
              >
                Undo choice…
              </button>
            )}
          </div>
          {confirmTrack === selection.track_id && (
            <div className="rounded-lg bg-gray-50 p-3 space-y-2">
              <p className="text-sm text-gray-700">
                {RESET_TEXT[String(keepStarted)] ?? RESET_TEXT.undefined}
                Progress, quiz results and submissions are kept and come back if
                they choose it again. They aren&apos;t notified.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => undo(selection.track_id)}
                  disabled={reset.isLoading}
                  className={PRIMARY}
                >
                  {reset.isLoading ? 'Undoing…' : 'Undo choice'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmTrack(null)}
                  className={SECONDARY}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function ChooseForLearner({ cohortId, studentId, status }) {
  const choose = useChooseForLearner(cohortId);
  const chosen = new Set((status.selections ?? []).map((s) => s.track_id));
  const options = (status.options ?? []).filter((o) => !chosen.has(o.track_id));
  const atLimit = status.choices_left === 0 && chosen.size > 0;
  const [trackId, setTrackId] = useState('');
  const [replaceId, setReplaceId] = useState('');
  const [step, setStep] = useState('idle');
  const [notQualified, setNotQualified] = useState(null);

  useEffect(() => {
    setTrackId('');
    setReplaceId('');
    setStep('idle');
    setNotQualified(null);
    // Reset when another learner opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // With a single choice there's only one thing to replace.
  const replaceTrack = replaceId || (chosen.size === 1 ? [...chosen][0] : '');

  if (!options.length) return null;

  const send = (skipQualification) =>
    choose.mutate(
      {
        studentId,
        trackId,
        replaceTrackId: atLimit ? replaceTrack : undefined,
        skipQualification,
      },
      {
        onSuccess: () => {
          setStep('idle');
          setNotQualified(null);
          setTrackId('');
          toast.success(
            'Specialization chosen. They’ve been sent the usual enrolment email.'
          );
        },
        onError: (error) => {
          if (errorCode(error) === 'SPEC_NOT_QUALIFIED' && !skipQualification) {
            setNotQualified(specError(error));
            setStep('override');
            return;
          }
          setStep('idle');
          toast.error(specError(error, "We couldn't choose for this learner."));
        },
      }
    );

  const ready = trackId && (!atLimit || replaceTrack);

  return (
    <section className="space-y-2">
      <Heading>Choose for this learner</Heading>
      <p className="text-xs text-gray-500">
        For learners who can&apos;t choose themselves. It enrols them and sends
        their usual enrolment email.
      </p>
      <select
        aria-label="Specialization to choose"
        value={trackId}
        onChange={(e) => {
          setTrackId(e.target.value);
          setStep('idle');
        }}
        className={`${INPUT} w-full`}
      >
        <option value="">Choose a specialization…</option>
        {options.map((option) => (
          <option key={option.track_id} value={option.track_id}>
            {option.name}
          </option>
        ))}
      </select>
      {atLimit && (
        <select
          aria-label="Choice to replace"
          value={replaceTrack}
          onChange={(e) => setReplaceId(e.target.value)}
          className={`${INPUT} w-full`}
        >
          <option value="">Replace which choice?</option>
          {status.selections.map((s) => (
            <option key={s.track_id} value={s.track_id}>
              Replace {s.track_name || s.track_id}
            </option>
          ))}
        </select>
      )}
      {step === 'idle' && (
        <button
          type="button"
          disabled={!ready}
          onClick={() => setStep('confirm')}
          className={PRIMARY}
        >
          Choose…
        </button>
      )}
      {step === 'confirm' && (
        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <p className="text-sm text-gray-700">
            Enrol them in{' '}
            <strong>{options.find((o) => o.track_id === trackId)?.name}</strong>
            {atLimit &&
              ` instead of ${
                status.selections.find((s) => s.track_id === replaceTrack)
                  ?.track_name || 'their current choice'
              }`}
            ?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => send(false)}
              disabled={choose.isLoading}
              className={PRIMARY}
            >
              {choose.isLoading ? 'Choosing…' : 'Choose'}
            </button>
            <button
              type="button"
              onClick={() => setStep('idle')}
              className={SECONDARY}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {step === 'override' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-sm text-amber-800">{notQualified}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => send(true)}
              disabled={choose.isLoading}
              className={PRIMARY}
            >
              {choose.isLoading ? 'Choosing…' : 'Choose anyway'}
            </button>
            <button
              type="button"
              onClick={() => setStep('idle')}
              className={SECONDARY}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function LearnerDrawer({
  cohortId,
  learner,
  keepStarted,
  onClose,
}) {
  const studentId = learner?.student_id;
  const { data, isLoading, error, refetch } = useLearner(cohortId, studentId);
  const status = data?.status;
  const hasApprovalRule = status?.rules?.some(
    (r) => r.type === 'manual_approval'
  );

  let content = null;
  if (isLoading) {
    content = (
      <div
        className="h-40 rounded bg-gray-100 animate-pulse"
        aria-label="Loading"
      />
    );
  } else if (error) {
    content = (
      <div className="space-y-3 text-sm text-gray-600">
        <p>{specError(error, "We couldn't load this learner.")}</p>
        <button type="button" onClick={() => refetch()} className={PRIMARY}>
          Retry
        </button>
      </div>
    );
  } else if (status) {
    content = (
      <div className="space-y-6">
        <section className="space-y-2">
          <Heading>What they see</Heading>
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800">
            {status.message ||
              (status.gated ? '–' : 'Qualification is off for this cohort.')}
          </p>
          <RuleList rules={status.rules} />
        </section>
        {hasApprovalRule && (
          <ApprovalPanel
            cohortId={cohortId}
            studentId={studentId}
            approval={data.approval}
          />
        )}
        <section className="space-y-2">
          <Heading>
            Specialization
            {status.max_choices > 1 && ` (up to ${status.max_choices})`}
          </Heading>
          <SelectionList
            cohortId={cohortId}
            studentId={studentId}
            selections={status.selections}
            keepStarted={keepStarted}
          />
        </section>
        <ChooseForLearner
          cohortId={cohortId}
          studentId={studentId}
          status={status}
        />
      </div>
    );
  }

  return (
    <Transition show={Boolean(studentId)} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-200"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="flex h-full w-screen max-w-lg flex-col bg-white shadow-xl">
              <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div className="min-w-0">
                  <Dialog.Title className="truncate text-base font-semibold text-gray-900">
                    {learner?.name || learner?.email || 'Learner'}
                  </Dialog.Title>
                  {learner?.name && learner?.email && (
                    <p className="truncate text-xs text-gray-500">
                      {learner.email}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">{content}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
